import React, { useMemo, useState } from "react";

import styles from "./CdekSelector.module.scss";
import {
  CdekPickupPoint,
  CdekTariff,
} from "../../../common/types/interfaces";
import { Button } from "../../../common/components/button/Button";
import { Search } from "../../../common/components/search/Search";

interface Props {
  tariffs: CdekTariff[];
  pickupPoints: CdekPickupPoint[];
  selectedTariff: CdekTariff | null;
  selectedPickupPoint: CdekPickupPoint | null;
  onSelectTariff: (tariff: CdekTariff) => void;
  onSelectPickupPoint: (point: CdekPickupPoint) => void;
}

export const CdekSelector: React.FC<Props> = ({
  tariffs,
  pickupPoints,
  selectedTariff,
  selectedPickupPoint,
  onSelectTariff,
  onSelectPickupPoint,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const courierTariffs = useMemo(
    () => tariffs.filter((t) => t.delivery_mode === 1),
    [tariffs],
  );

  const pickupTariffs = useMemo(
    () => tariffs.filter((t) => t.delivery_mode === 2 || t.delivery_mode === 7),
    [tariffs],
  );

  const isPickupSelected = !!selectedTariff && pickupTariffs.includes(selectedTariff);

  const filteredPickupPoints = useMemo(() => {
    if (!searchQuery.trim()) return pickupPoints;
    return pickupPoints.filter((point) =>
      point.location.address_full
        .toLowerCase()
        .includes(searchQuery.toLowerCase()),
    );
  }, [pickupPoints, searchQuery]);

  const visiblePickupPoints = useMemo(() => {
    if (!isPickupSelected) return [];
    if (showAll) return filteredPickupPoints;
    return filteredPickupPoints.slice(0, 3);
  }, [isPickupSelected, filteredPickupPoints, showAll]);

  const hasMorePoints = filteredPickupPoints.length > 3;

  const renderTariff = (tariff: CdekTariff) => {
    const isActive = selectedTariff?.tariff_code === tariff.tariff_code;
    return (
      <button
        key={tariff.tariff_code}
        type="button"
        className={`${styles.Card} ${isActive ? styles.Active : ""}`}
        onClick={() => onSelectTariff(tariff)}
      >
        <span className={styles.CardName}>{tariff.tariff_name}</span>
        <span className={styles.CardMeta}>
          {tariff.delivery_sum} ₽ · {tariff.period_min}–{tariff.period_max} дн.
        </span>
      </button>
    );
  };

  const renderPickupPoint = (point: CdekPickupPoint) => {
    const isActive = selectedPickupPoint?.code === point.code;
    return (
      <button
        key={point.code}
        type="button"
        className={`${styles.Card} ${isActive ? styles.Active : ""}`}
        onClick={() => onSelectPickupPoint(point)}
      >
        <span className={styles.CardName}>{point.name}</span>
        <span className={styles.CardMeta}>{point.location.address_full}</span>
        {point.work_time && (
          <span className={styles.CardExtra}>{point.work_time}</span>
        )}
      </button>
    );
  };

  return (
    <div className={styles.Container}>
      <h3 className={styles.Title}>Доставка СДЭК</h3>

      {isPickupSelected && pickupPoints.length > 0 && (
        <div className={styles.Section}>
          <h4 className={styles.Subtitle}>Пункты выдачи</h4>

          <Search
            value={searchQuery}
            onChange={setSearchQuery}
            clear={() => setSearchQuery("")}
            placeHolder="Поиск по адресу..."
          />

          <div className={styles.List}>
            {visiblePickupPoints.map(renderPickupPoint)}
          </div>

          {hasMorePoints && (
            <Button
              title={
                showAll
                  ? "Скрыть"
                  : `Показать все (${filteredPickupPoints.length})`
              }
              variant="secondary"
              onClick={() => setShowAll(!showAll)}
              fullWidth
            />
          )}
        </div>
      )}

      <div className={styles.List}>{courierTariffs.map(renderTariff)}</div>
    </div>
  );
};